import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { api } from "../lib/api";

const ASIC_REGISTER_URL =
  "https://connectonline.asic.gov.au/RegistrySearch/faces/landing/ProfessionalRegisters.jspx";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const ManagerRegister = ({ preview = 0 }) => {
  const [managers, setManagers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/managers/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "All") params.category = category;
    if (query.trim()) params.q = query.trim();
    api
      .get("/managers", { params })
      .then((r) => setManagers(r.data))
      .catch(() => setManagers([]))
      .finally(() => setLoading(false));
  }, [category, query]);

  const rows = preview > 0 ? managers.slice(0, preview) : managers;

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-4 mb-6 font-mono text-xs">
        <div className="flex flex-wrap gap-2" data-testid="directory-category-filters">
          {["All", ...categories].map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`filter-${slug(c)}`}
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 rounded border transition-colors ${
                category === c
                  ? "bg-cy text-navy border-cy font-bold"
                  : "bg-surface text-mut border-line hover:border-cy/50"
              }`}
            >
              {c.toUpperCase()}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="SEARCH MANAGER OR STRATEGY"
          data-testid="directory-search-input"
          className="md:ml-auto w-full md:w-72 p-2.5 rounded bg-navy border border-line text-white placeholder:text-slate-600 focus:border-cy outline-none"
        />
      </div>

      <div className="border border-line rounded-xl overflow-hidden font-mono text-xs" data-testid="directory-table">
        <table className="w-full text-left">
          <thead className="bg-surface text-mut border-b border-line">
            <tr>
              <th className="p-4">MANAGER ENTITY</th>
              <th className="p-4 hidden md:table-cell">STRATEGY CLASSIFICATION</th>
              <th className="p-4">AFSL</th>
              <th className="p-4 text-right">DOMAIN</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line bg-navy">
            {loading && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-mut" data-testid="directory-loading">
                  LOADING REGISTER&hellip;
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-mut" data-testid="directory-empty">
                  NO MATCHING MANAGERS
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((m) => (
                <tr key={m.id} className="hover:bg-surface/50" data-testid={`manager-row-${slug(m.name)}`}>
                  <td className="p-4">
                    <div className="font-bold text-white">{m.name}</div>
                    {m.entity && <div className="text-slate-500 text-[10px] mt-0.5">{m.entity}</div>}
                    <div className="text-slate-500 text-[10px] mt-0.5 md:hidden">{m.strategy}</div>
                  </td>
                  <td className="p-4 text-mut hidden md:table-cell">{m.strategy}</td>
                  <td className="p-4">
                    {m.afsl ? (
                      <span className="text-cy">{m.afsl}</span>
                    ) : (
                      <a
                        href={ASIC_REGISTER_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 underline decoration-dotted hover:text-cy transition-colors"
                        data-testid={`afsl-verify-${slug(m.name)}`}
                      >
                        Verify on ASIC
                      </a>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <a
                      href={`https://${m.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-400 hover:text-cy transition-colors inline-flex items-center gap-1"
                      data-testid={`website-${slug(m.name)}`}
                    >
                      {m.website}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
